/**
 * XSS Sanitization Utilities
 *
 * Provides functions to sanitize user input and prevent XSS attacks
 */
import DOMPurify from 'dompurify';

/**
 * Sanitize HTML content for safe rendering
 *
 * @param {string} html - Raw HTML string
 * @param {object} options - DOMPurify configuration
 * @returns {string} Sanitized HTML
 */
export function sanitizeHtml(html, options = {}) {
  if (!html) return '';

  const defaultConfig = {
    ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'br', 'p', 'span'],
    ALLOWED_ATTR: ['class'],
    KEEP_CONTENT: true,
    ...options
  };

  return DOMPurify.sanitize(html, defaultConfig);
}

/**
 * Sanitize text for display (strip all HTML)
 *
 * @param {string} text - Raw text
 * @returns {string} Plain text with HTML stripped
 */
export function sanitizeText(text) {
  if (!text) return '';

  // Strip ALL HTML tags
  return DOMPurify.sanitize(text, {
    ALLOWED_TAGS: [],
    KEEP_CONTENT: true
  });
}

/**
 * Sanitize user input before sending to API
 *
 * @param {string} input - User input
 * @param {number} maxLength - Maximum length
 * @returns {string} Sanitized input
 */
export function sanitizeInput(input, maxLength = 2200) {
  if (!input) return '';

  // Remove null bytes
  let sanitized = input.replace(/\0/g, '');

  // Remove control characters (except newlines and tabs)
  sanitized = sanitized.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '');

  // Enforce length limit
  if (sanitized.length > maxLength) {
    sanitized = sanitized.substring(0, maxLength);
  }

  return sanitized.trim();
}

/**
 * Validate and sanitize hex color
 *
 * @param {string} color - Hex color string
 * @returns {string} Valid hex color or default
 */
export function sanitizeHexColor(color) {
  if (!color) return '#FFFFFF';

  const hexPattern = /^#[0-9A-Fa-f]{6}$/;
  if (!hexPattern.test(color)) {
    console.warn(`Invalid hex color: ${color}, using default`);
    return '#FFFFFF';
  }

  return color.toUpperCase();
}

/**
 * Sanitize caption text before API submission
 *
 * @param {string} caption - Caption text
 * @returns {string} Sanitized caption
 */
export function sanitizeCaption(caption) {
  return sanitizeInput(caption, 2200);
}

/**
 * Sanitize brand TOV examples
 *
 * @param {string} example - TOV example text
 * @returns {string} Sanitized example
 */
export function sanitizeTovExample(example) {
  return sanitizeInput(example, 280);
}

/**
 * Sanitize brand additional instructions
 *
 * @param {string} instructions - Additional instructions
 * @returns {string} Sanitized instructions
 */
export function sanitizeInstructions(instructions) {
  return sanitizeInput(instructions, 500);
}
